<template>
  <private-gym-page
    id="admin-news-page"
    name="admin-news"
    :navbar="false"
    :has_fab="true"
  >
    <!--
      The shared PageTopNavbar is deliberately switched off. It carries the
      panel toggle and the logo, which is right for a member's landing surface
      and wrong here: this is a pushed detail screen and the operator needs a
      way back to where they came from.
    -->
    <f7-navbar title="Nieuws" back-link="Terug"></f7-navbar>

    <div class="admin-news-column">
      <f7-block-header>
        Berichten die leden op hun reserveringenpagina zien.
      </f7-block-header>

      <!--
        THREE MUTUALLY EXCLUSIVE STATES, driven by two flags and never by
        inferring state from an empty array.

        "we have not asked yet" and "we asked and there is nothing" look
        identical in `items`, and telling an operator there are no news items a
        beat before showing them four is the defect this v-if / v-else-if chain
        exists to prevent.
      -->

      <!-- 1. Loading. -->
      <f7-block v-if="loading" class="text-align-center">
        <!--
          The INLINE preloader, not the app-wide overlay: the overlay covers the
          navbar and swallows the back gesture, so page chrome and title render
          immediately behind this block instead.
        -->
        <f7-preloader></f7-preloader>
      </f7-block>

      <!-- 2. Failure. -->
      <f7-block v-else-if="error" strong inset>
        <!--
          PERSISTENT, NOT A TOAST. A load failure leaves the operator with
          nothing else to act on, so the message must not disappear on a timer.

          The screen dispatches no notification of its own: `news/fetch_all_admin`
          is deliberately not silent (43-06), so the global handler has already
          shown the one message this failure gets. A second would be a duplicate
          about the same request.

          The server's own error text is deliberately NOT rendered. It is
          English, unbounded, and names nothing the operator can act on; the
          authored Dutch copy below is the whole message.
        -->
        <p class="admin-news-heading">We konden de nieuwsberichten niet laden.</p>
        <p>Controleer je internetverbinding en probeer het opnieuw.</p>
        <f7-button fill @click="fetchNews">Opnieuw proberen</f7-button>
      </f7-block>

      <!-- 3a. Loaded, with nothing in it. -->
      <f7-block v-else-if="items.length === 0" strong inset>
        <!--
          A real empty state here, unlike the member surface, because this
          page's only purpose is the list. The body names the control the
          operator must reach for, and that control is on screen while they
          read it.
        -->
        <p class="admin-news-heading">Nog geen nieuwsberichten</p>
        <p>Maak je eerste bericht met de + onderaan de pagina.</p>
      </f7-block>

      <!-- 3b. Loaded, with rows. -->
      <f7-list v-else media-list class="list-strong list-outline list-dividers">
        <!--
          Rows are navigation only. Deleting an item happens on the composer, so
          there is one confirmation path rather than two to keep in sync — and
          the operator is already looking at what they are about to remove.

          The list is unbounded and unpaginated. The table has never held more
          than a handful of rows, and a cap would silently withhold a published
          item from the only screen that can edit it.
        -->
        <f7-list-item
          v-for="item in items"
          :key="item.id"
          :link="'/admin/news/' + item.id"
          :title="item.title"
          :subtitle="item.subtitle"
          :after="statusWord(item)"
          :text="dateText(item)"
        ></f7-list-item>
      </f7-list>
    </div>

    <pg-fab @click="f7router.navigate('/admin/news/new')"></pg-fab>
  </private-gym-page>
</template>

<script>
import PrivateGymPage from "../_layouts/PrivateGymPage.vue";
import PgFab from "../../components/general/PgFab.vue";

import moment from "moment/dist/moment";
import "moment/dist/locale/nl";
moment.locale("nl");

/**
 * The four status words, as a frozen vocabulary rather than four string
 * literals scattered through the methods below.
 *
 * A WORD AND NEVER A COLOURED MARK: a word survives greyscale, colour-vision
 * deficiency and a screen reader, and colour must never be the only signal.
 *
 * `GEEN_PERIODE` is the state the operator most needs to see, because such an
 * item is visible to nobody.
 */
const STATUS = Object.freeze({
  VISIBLE: "Nu zichtbaar",
  SCHEDULED: "Gepland",
  EXPIRED: "Verlopen",
  DATELESS: "Geen periode",
});

/** The formats the API is contracted to emit for a date column. */
const DATE_FORMATS = ["YYYY-MM-DD", moment.ISO_8601];

/**
 * Strict parse of a stored date, or null.
 *
 * Returning null rather than an invalid moment is what keeps `Invalid date` out
 * of the rendered row: every caller below checks for null first and falls back
 * to the dateless copy.
 *
 * @param {?string} value
 * @returns {?Object} a valid moment, or null
 */
function parseDate(value) {
  if (!value) {
    return null;
  }

  const parsed = moment(value, DATE_FORMATS, true);

  return parsed.isValid() ? parsed : null;
}

export default {
  components: { PrivateGymPage, PgFab },

  props: {
    f7route: Object,
    f7router: Object,
  },

  mixins: [],

  data: function () {
    return {
      /**
       * Starts TRUE, deliberately.
       *
       * `mounted()` runs after the first render, so a false default would paint
       * the empty state for one frame on every visit — an operator with four
       * articles would see "Nog geen nieuwsberichten" flash before their list.
       */
      loading: true,

      /**
       * Separate from `loading`, so a failed load is its own state rather than
       * an empty list. The two are never true together.
       */
      error: false,
    };
  },

  computed: {
    /**
     * The operator's list: every row, including the ones a member cannot see.
     *
     * `news/fetch_all_admin` reads `/news/all`, so scheduled, expired and
     * inverted-range rows are all here. An operator who cannot see next week's
     * scheduled item cannot edit it, and one who cannot see last month's cannot
     * delete it.
     *
     * SORTED DEFENSIVELY, over a copy. The SERVER is the primary ordering
     * authority — `/news/all` returns `visible_from` DESC then `id` DESC and
     * 43-02 measured that the id tie-break is load-bearing — so this re-sort
     * mirrors that order rather than inventing one, and exists only so a
     * contract regression on the server cannot silently scramble the screen.
     *
     * @returns {Array}
     */
    items() {
      return [...this.$store.getters["news/all"]].sort((left, right) => {
        const leftFrom = parseDate(left.visible_from);
        const rightFrom = parseDate(right.visible_from);

        // A row with no start date cannot exist under the current NOT NULL
        // schema (43-02 measured the insert being rejected). It is sorted last
        // rather than crashing the comparator, because the row that reaches
        // this branch is a contract regression and the screen should still
        // render it.
        if (leftFrom === null && rightFrom === null) {
          return right.id - left.id;
        }
        if (leftFrom === null) {
          return 1;
        }
        if (rightFrom === null) {
          return -1;
        }

        const byDate = rightFrom.valueOf() - leftFrom.valueOf();

        return byDate !== 0 ? byDate : right.id - left.id;
      });
    },
  },

  created() {
    //
  },

  mounted() {
    this.fetchNews();
  },

  methods: {
    /**
     * Loads every news item for the operator.
     *
     * Retry runs through this same method, which is what returns the screen to
     * its preloader instead of spinning inside the failure block — one loading
     * representation per screen, not two. A repeated failure updates the same
     * block rather than stacking a second one, because there is only ever one.
     *
     * The `.catch` is required rather than optional. `news/fetch_all_admin`
     * still rejects (43-06: suppression would hide the toast, never the
     * failure — and this call site does not suppress anything), so without a
     * handler the rejection is unhandled, which is console noise on top of the
     * one message the operator has already been shown.
     */
    fetchNews() {
      this.loading = true;
      this.error = false;

      this.$store
        .dispatch("news/fetch_all_admin", {})
        .catch(() => {
          this.error = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * One word describing where an item sits relative to today.
     *
     * An INVERTED range — `visible_to` before `visible_from`, which production
     * holds one of — reports `Verlopen` rather than `Gepland`. The server's
     * window is inclusive on both ends, so an inverted row matches nothing on
     * any day: it is visible to nobody now and never becomes visible. Calling
     * it `Gepland` would promise the operator a start date that never arrives.
     * The composer is where that is named explicitly, because that is the
     * screen where it can be fixed.
     *
     * @param {Object} item
     * @returns {string} one of STATUS
     */
    statusWord(item) {
      const from = parseDate(item.visible_from);
      const to = parseDate(item.visible_to);

      if (from === null || to === null) {
        return STATUS.DATELESS;
      }

      if (to.isBefore(from, "day")) {
        return STATUS.EXPIRED;
      }

      const today = moment();

      if (today.isBefore(from, "day")) {
        return STATUS.SCHEDULED;
      }

      if (today.isAfter(to, "day")) {
        return STATUS.EXPIRED;
      }

      return STATUS.VISIBLE;
    },

    /**
     * The row's date line, matching the status word above.
     *
     * Guards the moment call rather than formatting a null, so a legacy or
     * regressed row renders authored Dutch instead of `Invalid date`.
     *
     * @param {Object} item
     * @returns {string}
     */
    dateText(item) {
      const from = parseDate(item.visible_from);
      const to = parseDate(item.visible_to);

      if (from === null || to === null) {
        return "Geen begin- of einddatum";
      }

      const status = this.statusWord(item);

      if (status === STATUS.SCHEDULED) {
        return "Zichtbaar vanaf " + from.format("D MMMM YYYY");
      }

      if (status === STATUS.EXPIRED) {
        return "Verlopen op " + to.format("D MMMM YYYY");
      }

      return "Zichtbaar t/m " + to.format("D MMMM YYYY");
    },
  },

  watch: {
    //
  },
};
</script>

<style scoped>
/*
  Framework7 blocks and lists are full-bleed, and this phase must support the
  desktop-web build as well as the phone. A list spanning 1,400px reads badly.

  672px sits at --f7-card-expandable-tablet-width (670px), the widest the
  member's reading surface can ever be, so what the operator sees here is
  honest about how the article will land for a member.

  Below 768px the page is full-bleed exactly as every other page in the app is.
*/
@media (min-width: 768px) {
  .admin-news-column {
    max-width: 672px;
    margin-left: auto;
    margin-right: auto;
  }
}

/*
  The Heading role, taken entirely from Framework7's own tokens. No numeric size
  is authored here or anywhere else on this page: a fifth size on one screen is
  how a design system stops being one.
*/
.admin-news-heading {
  font-size: var(--f7-block-title-font-size);
  font-weight: var(--f7-block-title-font-weight);
  line-height: var(--f7-block-title-line-height);
  margin-top: 0;
  margin-bottom: 8px;
}
</style>
